// ════════════════════════════════════════════════════════════════════════════
// components/CRM/EtapaWorkflow/CierreGanadoForm.jsx
// Formulario completo para cerrar oportunidad ganada y crear proyecto/factura
// CON BOTÓN GENERAR NCF AL FINAL (DESPUÉS DE ANTICIPO)
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid,
  Stepper, Step, StepLabel, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Divider, Chip, Card, CardContent, Checkbox, FormControlLabel,
  Radio, RadioGroup, FormControl, FormLabel
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
  Receipt as NCFIcon,
  AutoAwesome as MagicIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';

const PASOS_PROYECTO = [
  'Resumen de Negociación',
  'Datos del Contrato',
  'Confirmación de Pagos',
  'Creación del Proyecto'
];

const PASOS_FACTURA = [
  'Resumen de Servicios',
  'Datos de la Factura',
  'Anticipo (Opcional)',
  'Crear Factura'
];



const CierreGanadoForm = ({ oportunidad, onGuardado, transicionExitosa, onTransicionCompleta }) => {
  const esObraCivil = oportunidad?.tipo_negocio === 'obra_civil';
  const esSaas = oportunidad?.tipo_negocio === 'saas';
  const PASOS = esObraCivil ? PASOS_PROYECTO : PASOS_FACTURA;
  const [pasoActivo, setPasoActivo] = useState(0);
  
  // Cerrar modal automáticamente cuando la transición fue confirmada por el backend
  // Cubre el flujo SaaS, donde onGuardado no llama onTransicionCompleta directamente
  useEffect(() => {
    if (transicionExitosa && onTransicionCompleta) {
      onTransicionCompleta();
    }
  }, [transicionExitosa]);

  // Estado para tipos de NCF
  const [tiposNCF, setTiposNCF] = useState([]);
  const [cargandoNCF, setCargandoNCF] = useState(false);
  const [generandoNCF, setGenerandoNCF] = useState(false);
  
  // Calcular partidas y totales
  const partidasFinales = useMemo(() => {
    if (oportunidad.cierre_detalle?.partidas_pactadas?.length > 0) {
      return oportunidad.cierre_detalle.partidas_pactadas;
    }
    return oportunidad.propuesta?.lineas || [];
  }, [oportunidad]);
  
  // Calcular totales con redondeo a 2 decimales
  const totales = useMemo(() => {
    const subtotal = partidasFinales.reduce((sum, p) => {
      const total = p.total_pactado || p.total || 0;
      return sum + parseFloat(total);
    }, 0);
    
    const subtotalRedondeado = Math.round(subtotal * 100) / 100;
    const itbis = Math.round(subtotalRedondeado * 0.18 * 100) / 100;
    const total = Math.round((subtotalRedondeado + itbis) * 100) / 100;
    
    return { 
      subtotal: subtotalRedondeado, 
      itbis, 
      total 
    };
  }, [partidasFinales]);
  
  const numeroContratoAuto = `CONT-${String(oportunidad.id).padStart(6, '0')}`;
  
  const fechaVencimientoDefault = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  })();

  const [datos, setDatos] = useState({
    contrato_numero: numeroContratoAuto,
    fecha_firma: new Date().toISOString().split('T')[0],
    fecha_vencimiento: fechaVencimientoDefault,
    monto_final: totales.total,
    margen_real: '',
    requiere_anticipo: false,
    anticipo_recibido: '',
    fecha_anticipo: new Date().toISOString().split('T')[0],
    metodo_pago: 'transferencia',
    proyecto_nombre: `${oportunidad.prospecto?.nombre} - Proyecto`,
    observaciones: '',
    partidas_pactadas: partidasFinales,
    
    // Opciones de NCF
    tipo_comprobante: 'sin_ncf',
    tipo_ncf: '',
    ncf: ''
  });
  
  const [creando, setCreando] = useState(false);
  const [resultado, setResultado] = useState(null);

  // ✅ Cargar tipos de NCF disponibles
  useEffect(() => {
    const cargarTiposNCF = async () => {
      if (esObraCivil) return;
      
      setCargandoNCF(true);
      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3100';
        
        const response = await axios.get(
          `${API_URL}/api/ncf/secuencias`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const activas = response.data.filter(s => s.activo === true && String(s.tipo_ncf) !== '11');
        setTiposNCF(activas);
      } catch (error) {
        console.error('Error cargando tipos NCF:', error);
        toast.error('Error al cargar tipos de comprobante fiscal');
      } finally {
        setCargandoNCF(false);
      }
    };
    
    cargarTiposNCF();
  }, [esObraCivil]);

  // ✅ Función para generar NCF
  const generarSiguienteNCF = async (tipoNCF) => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3100';
      
      const response = await axios.post(
        `${API_URL}/api/ncf/generar`,
        { tipo_ncf: tipoNCF },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      return {
        ncf: response.data.ncf,
        numero: response.data.secuencia
      };
      
    } catch (error) {
      console.error('Error generando NCF:', error);
      toast.error(error.response?.data?.error || 'Error al generar NCF');
      return null;
    }
  };

  // ✅ Manejador para botón GENERAR NCF
  const handleGenerarNCF = async () => {
    if (!datos.tipo_ncf) {
      toast.warning('Primero selecciona un tipo de NCF');
      return;
    }
    
    setGenerandoNCF(true);
    const resultado = await generarSiguienteNCF(datos.tipo_ncf);
    
    if (resultado) {
      setDatos(prev => ({ ...prev, ncf: resultado.ncf }));
      toast.success(`✅ NCF generado: ${resultado.ncf}`);
    }
    
    setGenerandoNCF(false);
  };

  // Actualizar monto_final cuando cambien los totales
  useEffect(() => {
    setDatos(prev => ({ 
      ...prev, 
      monto_final: Math.round(totales.total * 100) / 100 
    }));
  }, [totales.total]);

  const handleSiguiente = () => {
    if (pasoActivo === 0) {
      setPasoActivo(1);
    } else if (pasoActivo === 1) {
      if (!datos.fecha_firma) {
        toast.error('Seleccione la fecha de firma del contrato');
        return;
      }
      
      // ✅ Validación básica de NCF (sin generar aún)
      if (!esObraCivil && datos.tipo_comprobante === 'con_ncf' && !datos.tipo_ncf) {
        toast.error('Seleccione el tipo de comprobante fiscal');
        return;
      }
      
      setPasoActivo(2);
    } else if (pasoActivo === 2) {
      // Validar solo si requiere anticipo
      if (datos.requiere_anticipo) {
        if (!datos.anticipo_recibido || !datos.fecha_anticipo) {
          toast.error('Complete los datos del pago inicial');
          return;
        }
      }
      setPasoActivo(3);
    }
  };

  const handleAnterior = () => {
    setPasoActivo(prev => prev - 1);
  };

  const getDescripcionNCF = (tipo) => {
    const descripciones = {
      '01': 'Crédito Fiscal (B2B)',
      '02': 'Consumo (B2C)',
      '03': 'Nota de Débito',
      '04': 'Nota de Crédito',
      '11': 'Comprobante de Compras',
      '12': 'Registro Único de Ingresos',
      '13': 'Gastos Menores',
      '14': 'Regímenes Especiales',
      '15': 'Gubernamental'
    };
    return descripciones[tipo] || 'Otro';
  };

  const handleCrearProyecto = async () => {
    setCreando(true);

    try {
      // ✅ Validar que se haya generado NCF si es necesario
      if (!esObraCivil && datos.tipo_comprobante === 'con_ncf' && !datos.ncf) {
        toast.error('Debe generar el NCF con el botón antes de crear la factura');
        setCreando(false);
        return;
      }

      const datosNCF = {};
      if (!esObraCivil && datos.tipo_comprobante === 'con_ncf') {
        datosNCF.tipo_ncf = datos.tipo_ncf;
        datosNCF.ncf = datos.ncf;
        datosNCF.requiere_ncf = true;
      } else {
        datosNCF.requiere_ncf = false;
      }
      
      const datosParaEnviar = {
        cierre_detalle: {
          fecha_cierre: datos.fecha_firma,
          fecha_firma: datos.fecha_firma,
          fecha_vencimiento: datos.fecha_vencimiento,
          monto_final: Math.round(parseFloat(datos.monto_final) * 100) / 100,
          margen_real: datos.margen_real ? Math.round(parseFloat(datos.margen_real) * 100) / 100 : 0,
          contrato_numero: datos.contrato_numero,
          partidas_pactadas: datos.partidas_pactadas.map(p => ({
            ...p,
            precio_pactado: Math.round(parseFloat(p.precio_pactado || 0) * 100) / 100,
            total_pactado: Math.round(parseFloat(p.total_pactado || 0) * 100) / 100,
            precio_unitario: Math.round(parseFloat(p.precio_unitario || 0) * 100) / 100,
            total: Math.round(parseFloat(p.total || 0) * 100) / 100
          })),
          anticipo_recibido: datos.requiere_anticipo 
            ? Math.round(parseFloat(datos.anticipo_recibido || 0) * 100) / 100 
            : 0,
          fecha_anticipo: datos.fecha_anticipo,
          metodo_pago: datos.metodo_pago,
          observaciones: datos.observaciones,
          concepto: datos.observaciones,
          ...datosNCF
        },
        proyecto_nombre: datos.proyecto_nombre
      };



      console.log('📤 Enviando datos de cierre:', datosParaEnviar);

// 🔥 NUEVOS LOGS PARA DEBUGGEAR NCF
console.log('🔥 VERIFICANDO NCF:');
console.log('- tipo_ncf (datos):', datos.tipo_ncf);
console.log('- ncf (datos):', datos.ncf);
console.log('- datosNCF:', datosNCF);
console.log('- tipo_ncf en cierre_detalle:', datosParaEnviar.cierre_detalle.tipo_ncf);
console.log('- ncf en cierre_detalle:', datosParaEnviar.cierre_detalle.ncf);
      await onGuardado(datosParaEnviar);

      const mensajeExito = esObraCivil
        ? '¡Proyecto creado exitosamente!'
        : `¡Factura creada exitosamente! ${datos.tipo_comprobante === 'con_ncf' ? `NCF: ${datos.ncf}` : 'Sin comprobante fiscal.'}`;

      setResultado({ exito: true, mensaje: mensajeExito });

      toast.success(esObraCivil
        ? '✅ Oportunidad cerrada y proyecto creado'
        : `✅ Oportunidad cerrada y factura creada ${datos.tipo_comprobante === 'con_ncf' ? `con NCF ${datos.ncf}` : 'sin NCF'}`);

      setTimeout(() => {
        if (onTransicionCompleta) onTransicionCompleta();
      }, 2000);

    } catch (error) {
      console.error('Error en cierre de oportunidad:', error);
      setResultado({
        exito: false,
        mensaje: error.response?.data?.error ||
          (esObraCivil ? 'Error al crear el proyecto' : 'Error al crear la factura')
      });
      toast.error(esObraCivil ? '❌ Error al crear el proyecto' : '❌ Error al crear la factura');
    } finally {
      setCreando(false);
    }
  };

  const renderPaso = () => {
    switch (pasoActivo) {
      // ════════════════════════════════════════════════════════════════
      // PASO 0: Resumen de Negociación
      // ════════════════════════════════════════════════════════════════
      case 0:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Resumen Final de la Negociación</strong>
              <br />
              Revise las partidas y montos acordados antes de proceder con el cierre.
            </Alert>

            <Card sx={{ mb: 3, bgcolor: '#f8fafc' }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Cliente</Typography>
                    <Typography variant="h6">{oportunidad.prospecto?.nombre}</Typography>
                    {oportunidad.prospecto?.empresa && (
                      <Typography variant="body2">{oportunidad.prospecto.empresa}</Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Contacto</Typography>
                    <Typography variant="body2">📧 {oportunidad.prospecto?.email}</Typography>
                    <Typography variant="body2">📞 {oportunidad.prospecto?.telefono}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#667eea' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Código</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Descripción</TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Cantidad</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Precio Unit.</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {partidasFinales.map((partida, index) => {
                    const precio = partida.precio_pactado || partida.precio_unitario || 0;
                    const cantidad = partida.cantidad || 0;
                    const total = partida.total_pactado || partida.total || (precio * cantidad);
                    
                    return (
                      <TableRow key={index}>
                        <TableCell>{partida.linea_id || partida.codigo || ''}</TableCell>
                        <TableCell>{partida.descripcion || partida.nombre || ''}</TableCell>
                        <TableCell align="center">{cantidad}</TableCell>
                        <TableCell align="right">
                          ${parseFloat(precio).toLocaleString('es-DO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </TableCell>
                        <TableCell align="right">
                          ${parseFloat(total).toLocaleString('es-DO', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Paper sx={{ p: 3, bgcolor: '#f8fafc', maxWidth: 400, ml: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotal:</Typography>
                <Typography>
                  ${totales.subtotal.toLocaleString('es-DO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>ITBIS (18%):</Typography>
                <Typography>
                  ${totales.itbis.toLocaleString('es-DO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 'bold' }}>
                  TOTAL:
                </Typography>
                <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 'bold' }}>
                  ${totales.total.toLocaleString('es-DO', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </Typography>
              </Box>
            </Paper>
          </Box>
        );
      
      // ════════════════════════════════════════════════════════════════
      // PASO 1: Datos del Contrato / Factura
      // ════════════════════════════════════════════════════════════════
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="warning" icon={<AssignmentIcon />}>
                {esObraCivil
                  ? 'Complete los datos del contrato para formalizar el cierre'
                  : 'Complete los datos de la factura para formalizar el cierre'}
              </Alert>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Número de Contrato"
                value={datos.contrato_numero}
                InputProps={{
                  readOnly: true,
                  startAdornment: <Box sx={{ mr: 1 }}>📄</Box>
                }}
                helperText="Generado automáticamente desde oportunidad"
                sx={{ '& .MuiInputBase-input': { bgcolor: '#f8fafc', fontWeight: 'bold', color: '#667eea' } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label={esObraCivil ? 'Fecha de Firma' : 'Fecha de Emisión'}
                type="date"
                value={datos.fecha_firma}
                onChange={(e) => setDatos({...datos, fecha_firma: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label={esObraCivil ? 'Monto Final del Contrato' : 'Monto Total'}
                type="number"
                value={datos.monto_final}
                onChange={(e) => setDatos({...datos, monto_final: e.target.value})}
                InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>RD$</Box> }}
                helperText="Monto total acordado"
              />
            </Grid>

            {esObraCivil ? (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Margen de Utilidad (%)"
                    type="number"
                    value={datos.margen_real}
                    onChange={(e) => setDatos({...datos, margen_real: e.target.value})}
                    placeholder="25"
                    helperText="Porcentaje de ganancia estimado"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nombre del Proyecto"
                    value={datos.proyecto_nombre}
                    onChange={(e) => setDatos({...datos, proyecto_nombre: e.target.value})}
                    helperText="Nombre con el que se creará el proyecto en el sistema"
                  />
                </Grid>
              </>
            ) : (
              <>
                {/* ✅ SECCIÓN DE COMPROBANTE FISCAL - SOLO SELECCIÓN DE TIPO */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, bgcolor: '#f8fafc', mb: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#667eea' }}>
                      <NCFIcon /> Comprobante Fiscal (NCF)
                    </Typography>
                    
                    <FormControl component="fieldset" sx={{ mb: 3 }}>
                      <FormLabel component="legend">¿La factura requiere NCF?</FormLabel>
                      <RadioGroup
                        row
                        value={datos.tipo_comprobante}
                        onChange={(e) => setDatos({
                          ...datos, 
                          tipo_comprobante: e.target.value,
                          tipo_ncf: '',
                          ncf: ''
                        })}
                      >
                        <FormControlLabel 
                          value="sin_ncf" 
                          control={<Radio />} 
                          label="Sin NCF (Consumo final / No requiere)" 
                        />
                        <FormControlLabel 
                          value="con_ncf" 
                          control={<Radio />} 
                          label="Con NCF (Cliente requiere comprobante fiscal)" 
                        />
                      </RadioGroup>
                    </FormControl>
                    
                    {datos.tipo_comprobante === 'con_ncf' && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            required
                            select
                            label="Tipo de NCF"
                            value={datos.tipo_ncf}
                            onChange={(e) => {
                              setDatos(prev => ({ 
                                ...prev, 
                                tipo_ncf: e.target.value,
                                ncf: '' // Limpiar NCF al cambiar tipo
                              }));
                            }}
                            SelectProps={{ native: true }}
                            helperText="Seleccione el tipo de comprobante (el NCF se generará al final)"
                            disabled={cargandoNCF}
                          >
                            <option value="">Seleccionar tipo</option>
                            {tiposNCF.map(tipo => (
                              <option key={tipo.id} value={tipo.tipo_ncf}>
                                {tipo.tipo_ncf} - {getDescripcionNCF(tipo.tipo_ncf)} 
                                (Disponibles: {tipo.secuencia_final - tipo.secuencia_actual})
                              </option>
                            ))}
                          </TextField>
                        </Grid>
                      </Grid>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Fecha de Vencimiento"
                    type="date"
                    value={datos.fecha_vencimiento}
                    onChange={(e) => setDatos({...datos, fecha_vencimiento: e.target.value})}
                    InputLabelProps={{ shrink: true }}
                    helperText="Fecha límite de pago (default: 30 días)"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={esObraCivil ? 'Observaciones del Cierre' : 'Concepto / Observaciones'}
                value={datos.observaciones}
                onChange={(e) => setDatos({...datos, observaciones: e.target.value})}
                placeholder={esObraCivil
                  ? 'Condiciones especiales, acuerdos adicionales, etc.'
                  : 'Descripción de los servicios prestados, condiciones adicionales, etc.'}
              />
            </Grid>
          </Grid>
        );
      
      // ════════════════════════════════════════════════════════════════
      // PASO 2: Confirmación de Pagos / Anticipo + BOTÓN GENERAR NCF
      // ════════════════════════════════════════════════════════════════
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" icon={<BankIcon />}>
                {datos.requiere_anticipo
                  ? 'Registre el pago inicial recibido'
                  : esObraCivil
                    ? 'Configure si este proyecto requiere un anticipo inicial o si los pagos serán únicamente por certificaciones de obra'
                    : 'Configure si se recibió un anticipo inicial por los servicios'}
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 3, bgcolor: '#f8fafc', border: '2px solid #e2e8f0' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={datos.requiere_anticipo}
                      onChange={(e) => setDatos({
                        ...datos,
                        requiere_anticipo: e.target.checked,
                        anticipo_recibido: e.target.checked ? datos.anticipo_recibido : '',
                        fecha_anticipo: e.target.checked ? datos.fecha_anticipo : new Date().toISOString().split('T')[0],
                        metodo_pago: e.target.checked ? datos.metodo_pago : 'transferencia'
                      })}
                      sx={{
                        '& .MuiSvgIcon-root': { fontSize: 28 },
                        color: '#667eea',
                        '&.Mui-checked': { color: '#667eea' }
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {esObraCivil ? 'Este proyecto requiere anticipo inicial' : 'Se recibió un anticipo inicial'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {datos.requiere_anticipo
                          ? `Se registrará el pago inicial antes de crear ${esObraCivil ? 'el proyecto' : 'la factura'}`
                          : esObraCivil
                            ? 'Los pagos se manejarán únicamente a través de certificaciones de obra'
                            : 'Los pagos se manejarán desde el módulo de Facturación'}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            </Grid>
            
            {datos.requiere_anticipo && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Anticipo Recibido"
                    type="number"
                    value={datos.anticipo_recibido}
                    onChange={(e) => setDatos({...datos, anticipo_recibido: e.target.value})}
                    InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>RD$</Box> }}
                    helperText="Monto del pago inicial recibido"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Fecha de Pago"
                    type="date"
                    value={datos.fecha_anticipo}
                    onChange={(e) => setDatos({...datos, fecha_anticipo: e.target.value})}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Método de Pago"
                    value={datos.metodo_pago}
                    onChange={(e) => setDatos({...datos, metodo_pago: e.target.value})}
                    SelectProps={{ native: true }}
                  >
                    <option value="transferencia">Transferencia Bancaria</option>
                    <option value="cheque">Cheque</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="tarjeta">Tarjeta de Crédito</option>
                  </TextField>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, bgcolor: '#f0fdf4', border: '1px solid #86efac' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#16a34a' }}>
                      💰 Resumen del Pago Inicial
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Monto Total del Contrato:</Typography>
                        <Typography variant="h6">
                          ${parseFloat(datos.monto_final || 0).toLocaleString('es-DO')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Anticipo Recibido:</Typography>
                        <Typography variant="h6" sx={{ color: '#16a34a' }}>
                          ${parseFloat(datos.anticipo_recibido || 0).toLocaleString('es-DO')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" color="text.secondary">Porcentaje Recibido:</Typography>
                        <Typography variant="h6">
                          {datos.monto_final > 0 
                            ? ((parseFloat(datos.anticipo_recibido || 0) / parseFloat(datos.monto_final)) * 100).toFixed(1)
                            : 0}%
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </>
            )}
            
            {!datos.requiere_anticipo && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: '#fef3c7', border: '1px solid #fbbf24' }}>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    ℹ️ <strong>Sin anticipo inicial</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {esObraCivil
                      ? 'El proyecto se creará sin registro de pago inicial. Los pagos se manejarán a través del módulo de certificaciones de obra según el avance del proyecto.'
                      : 'La factura se creará sin pago inicial registrado. Los pagos se manejarán desde el módulo de Facturación.'}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* ✅ BOTÓN GENERAR NCF - AL FINAL DE LA SECCIÓN DE ANTICIPO */}
            {!esObraCivil && datos.tipo_comprobante === 'con_ncf' && (
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: '#f0f4ff', border: '2px dashed #667eea' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#667eea' }}>
                    <MagicIcon /> Generación de NCF
                  </Typography>
                  
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <TextField
                        fullWidth
                        label="NCF"
                        value={datos.ncf || 'Aún no generado'}
                        InputProps={{ readOnly: true }}
                        helperText={datos.ncf ? 'NCF listo para usar' : 'Haga clic en GENERAR para obtener el NCF'}
                        sx={{
                          '& .MuiInputBase-input': datos.ncf ? { 
                            fontWeight: 'bold', 
                            color: '#16a34a',
                            fontSize: '1.1rem'
                          } : {}
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleGenerarNCF}
                        disabled={!datos.tipo_ncf || generandoNCF}
                        sx={{
                          height: '56px',
                          background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          '&:hover': {
                            background: 'linear-gradient(45deg, #5a67d8 30%, #6b46a1 90%)',
                          },
                          '&:disabled': {
                            background: '#ccc'
                          }
                        }}
                      >
                        {generandoNCF ? <CircularProgress size={24} color="inherit" /> : '🎲 GENERAR NCF'}
                      </Button>
                    </Grid>
                    {datos.ncf && (
                      <Grid item xs={12}>
                        <Alert severity="success" sx={{ mt: 1 }}>
                          ✅ NCF generado: <strong>{datos.ncf}</strong> - Este número se asignará a la factura
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            )}
          </Grid>
        );
      
      // ════════════════════════════════════════════════════════════════
      // PASO 3: Confirmación y creación
      // ════════════════════════════════════════════════════════════════
      case 3:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {!creando && !resultado && (
              <>
                {esObraCivil
                  ? <BusinessIcon sx={{ fontSize: 80, color: '#667eea', mb: 3 }} />
                  : <ReceiptIcon sx={{ fontSize: 80, color: '#667eea', mb: 3 }} />
                }
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {esObraCivil ? '¿Listo para crear el Proyecto?' : '¿Listo para crear la Factura?'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
                  {esObraCivil
                    ? 'Se creará automáticamente un proyecto en el sistema con todas las partidas, presupuesto y datos del contrato.'
                    : `Se creará una factura en borrador con todos los servicios cotizados. ${
                        datos.tipo_comprobante === 'con_ncf' 
                          ? `NCF: ${datos.ncf}` 
                          : 'Sin comprobante fiscal.'
                      }`}
                </Typography>

                <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', textAlign: 'left', bgcolor: '#f8fafc' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#667eea', mb: 3 }}>
                    {esObraCivil ? '📋 Datos del Proyecto' : '🧾 Datos de la Factura'}
                  </Typography>

                  {esObraCivil && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Nombre del Proyecto:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{datos.proyecto_nombre}</Typography>
                    </Box>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Cliente:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {oportunidad.prospecto?.nombre}
                      {oportunidad.prospecto?.empresa && ` - ${oportunidad.prospecto.empresa}`}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {esObraCivil ? 'Presupuesto Total:' : 'Total Factura:'}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#16a34a' }}>
                      RD${parseFloat(datos.monto_final).toLocaleString('es-DO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {esObraCivil ? 'Partidas Incluidas:' : 'Servicios / Ítems:'}
                    </Typography>
                    <Typography variant="body1">{partidasFinales.length} conceptos</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Número de Contrato:</Typography>
                    <Typography variant="body1">{datos.contrato_numero}</Typography>
                  </Box>

                  {!esObraCivil && datos.tipo_comprobante === 'con_ncf' && datos.ncf && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Comprobante Fiscal:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#16a34a' }}>
                        {datos.ncf}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Tipo {datos.tipo_ncf} - {getDescripcionNCF(datos.tipo_ncf)}
                      </Typography>
                    </Box>
                  )}

                  {!esObraCivil && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Vence:</Typography>
                      <Typography variant="body1">{datos.fecha_vencimiento}</Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {datos.requiere_anticipo ? 'Anticipo Recibido:' : 'Modalidad de Pago:'}
                    </Typography>
                    {datos.requiere_anticipo ? (
                      <Typography variant="body1" sx={{ color: '#16a34a', fontWeight: 'bold' }}>
                        RD${parseFloat(datos.anticipo_recibido || 0).toLocaleString('es-DO', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </Typography>
                    ) : (
                      <Typography variant="body1" sx={{ color: '#f59e0b', fontWeight: 'bold' }}>
                        {esObraCivil ? 'Pago por certificaciones de obra' : 'Pago según factura emitida'}
                      </Typography>
                    )}
                  </Box>
                </Paper>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleCrearProyecto}
                  disabled={creando || (datos.tipo_comprobante === 'con_ncf' && !datos.ncf)}
                  sx={{
                    mt: 4, px: 6, py: 1.5,
                    bgcolor: '#16a34a',
                    fontSize: '1.1rem',
                    '&:hover': { bgcolor: '#15803d' },
                    '&:disabled': { bgcolor: '#ccc' }
                  }}
                >
                  {esObraCivil
                    ? '🚀 Crear Proyecto y Cerrar Oportunidad'
                    : '🧾 Crear Factura y Cerrar Oportunidad'}
                </Button>
              </>
            )}

            {creando && (
              <Box>
                <CircularProgress size={60} sx={{ color: '#667eea', mb: 3 }} />
                <Typography variant="h6">
                  {esObraCivil ? 'Creando proyecto...' : 'Creando factura...'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Por favor espere mientras se configuran todos los datos
                </Typography>
              </Box>
            )}

            {resultado && (
              <Alert
                severity={resultado.exito ? 'success' : 'error'}
                sx={{ maxWidth: 600, mx: 'auto', fontSize: '1.1rem' }}
                icon={resultado.exito ? <CheckIcon fontSize="large" /> : undefined}
              >
                {resultado.mensaje}
                {resultado.exito && (
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Redirigiendo al dashboard...
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        );
      
      default:
        return null;
    }
  };

  // ── FLUJO SAAS: solo confirmación, sin crear proyecto ni factura ────────
  if (esSaas) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CheckIcon sx={{ fontSize: 64, color: '#10b981', mb: 2 }} />
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Oportunidad SaaS Ganada
        </Typography>
        <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
          <strong>Activar empresa manualmente en el SuperAdmin</strong>
          <br />
          Esta oportunidad SaaS no genera proyecto ni factura automáticamente.
          Una vez confirmado el pago, activa la empresa desde el panel de SuperAdmin.
        </Alert>
        <Button
          variant="contained"
          size="large"
          sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, fontWeight: 'bold', px: 4 }}
          onClick={() => onGuardado({ tipo_cierre: 'saas' })}
        >
          Confirmar Cierre Ganado
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stepper activeStep={pasoActivo} sx={{ mb: 4 }}>
        {PASOS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 400 }}>
        {renderPaso()}
      </Box>

      {pasoActivo < 3 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: '1px solid #e2e8f0' }}>
          <Button
            disabled={pasoActivo === 0}
            onClick={handleAnterior}
            variant="outlined"
          >
            Anterior
          </Button>
          <Button
            variant="contained"
            onClick={handleSiguiente}
            sx={{ bgcolor: '#667eea', '&:hover': { bgcolor: '#5568d3' } }}
          >
            {pasoActivo === 2 ? 'Revisar y Crear' : 'Siguiente'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CierreGanadoForm;
